import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { useTenantBrand, BrandMark, BrandBar, PoweredBy } from "@/components/candidate/CandidateBrand";
import { Mic, MicOff, Loader2, ArrowRight } from "lucide-react";

// Browser Web Speech API for STT + speechSynthesis for TTS
export default function VoiceRoom() {
  const { token } = useParams();
  const brand = useTenantBrand(token);
  const nav = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [transcript, setTranscript] = useState([]); // {role, text}
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [ended, setEnded] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [partialText, setPartialText] = useState("");
  const recRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.post(`/voice/${token}/start`);
        if (!mounted) return;
        setSessionId(data.session_id);
        setTranscript([{ role: "assistant", text: data.ai_message }]);
        speak(data.ai_message);
      } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    })();
    return () => { mounted = false; try { window.speechSynthesis.cancel(); } catch(_){} };
  }, [token]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [transcript, partialText]);

  const speak = (text) => {
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.02; u.pitch = 1.0;
      const voices = window.speechSynthesis.getVoices();
      const v = voices.find(v => v.lang?.startsWith("en") && /female|samantha|zira|google us english/i.test(v.name)) || voices.find(v => v.lang?.startsWith("en"));
      if (v) u.voice = v;
      window.speechSynthesis.speak(u);
    } catch(_) {}
  };

  const startListen = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input isn't supported in this browser. Try Chrome / Edge.");
      return;
    }
    const r = new SR();
    r.continuous = true; r.interimResults = true; r.lang = "en-US";
    let final = "";
    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t + " "; else interim += t;
      }
      setPartialText(final + interim);
    };
    r.onerror = (e) => { if (e.error !== "no-speech") toast.error(`Mic: ${e.error}`); };
    r.onend = () => { setListening(false); if (final.trim()) sendTurn(final.trim()); setPartialText(""); };
    recRef.current = r;
    r.start();
    setListening(true);
    try { window.speechSynthesis.cancel(); } catch(_) {}
  };

  const stopListen = () => { try { recRef.current?.stop(); } catch(_) {} };

  const sendTurn = async (text) => {
    setTranscript((p) => [...p, { role: "user", text }]);
    setThinking(true);
    try {
      const { data } = await api.post("/voice/turn", { session_id: sessionId, candidate_text: text });
      setTranscript((p) => [...p, { role: "assistant", text: data.ai_message }]);
      speak(data.ai_message);
      if (data.ended) setEnded(true);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setThinking(false); }
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      await api.post("/voice/complete", { session_id: sessionId });
      toast.success("Telephonic interview complete");
      // Decide next route based on this job's enabled stages.
      try {
        const { data } = await api.get(`/invite/${token}`);
        const stages = data?.job?.stages || { telephonic: true, video: true, coding: true };
        if (stages.video) nav(`/invite/${token}/video`);
        else if (stages.coding) nav(`/invite/${token}/coding`);
        else nav(`/invite/${token}/done`);
      } catch {
        nav(`/invite/${token}/video`);
      }
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setFinalizing(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="voice-room">
      <header className="border-b border-[color:var(--bd-sub)] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4 min-w-0"><BrandMark tenant={brand} /><div className="text-xs uppercase tracking-wider text-slate-500 shrink-0">Telephonic Interview</div></div>
        <div className="flex items-center gap-2 text-xs text-rose-400"><span className="tp-rec-pulse"/> Recording</div>
      </header>

      <div className="flex-1 max-w-3xl mx-auto w-full px-6 py-8 flex flex-col">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-cyan-400 grid place-items-center">
            <Mic className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold" data-testid="voice-recruiter-name">{brand?.name ? `${brand.name} Recruiter` : "AI Recruiter"}</div>
            <div className="text-xs text-slate-500">Speak naturally — I'll follow up when needed.</div>
          </div>
          <div className="ml-auto">
            {thinking ? <div className="text-xs text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin"/>Thinking…</div> :
             listening ? <div className="tp-wave"><span/><span/><span/><span/><span/><span/><span/><span/><span/></div> : null}
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto pb-6" data-testid="voice-transcript">
          {transcript.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-indigo-600 text-white" : "bg-[color:var(--bg-card)] border border-[color:var(--bd-sub)] text-slate-100"}`}>
                {m.text}
              </div>
            </div>
          ))}
          {partialText && (
            <div className="flex justify-end">
              <div className="max-w-[80%] px-4 py-3 rounded-2xl text-sm bg-indigo-600/40 text-white/80 italic">{partialText}</div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="tp-hairline mb-4" />
        <div className="flex items-center justify-between">
          {!ended ? (
            <button
              data-testid="voice-mic-toggle"
              onClick={listening ? stopListen : startListen}
              disabled={thinking || !sessionId}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-colors ${listening ? "bg-rose-600 hover:bg-rose-500 text-white" : "bg-indigo-600 hover:bg-indigo-500 text-white"} disabled:opacity-60`}
            >
              {listening ? <><MicOff className="w-4 h-4"/> Stop & Send</> : <><Mic className="w-4 h-4"/> Hold to answer</>}
            </button>
          ) : (
            <div className="text-sm text-emerald-400">Screening complete. Ready to continue.</div>
          )}
          <button data-testid="voice-finish" onClick={finalize} disabled={!sessionId || finalizing} className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-[color:var(--bd-hi)] hover:bg-white/5 disabled:opacity-60">
            {finalizing ? <Loader2 className="w-4 h-4 animate-spin"/> : null}
            {ended ? "Continue" : "End & Continue"} <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
        <div className="text-xs text-slate-500 mt-3">Tip: click "Hold to answer", speak, then click "Stop & Send". Best in Chrome/Edge.</div>
      </div>
      <PoweredBy />
    </div>
  );
}
